// src/pages/ContentTaskDetailPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  doc, getDoc, serverTimestamp, Timestamp,
  getDocs, collection, query, orderBy, onSnapshot, writeBatch, arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, agencyFunctions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatRelativeTime } from '../utils/styleUtils';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getStatusLabel, getPlatformLabel, getContentTypeLabel } from '../utils/socialEnumLabels';
import styles from '../styles/ContentTaskDetailPage.module.css';

// Roles
const managerRoles = ['Manager', 'Accounts Manager'];
const viewRoles = ['Manager', 'Accounts Manager', 'Sales', 'Content Creator'];
const cancelRoles = ['Manager', 'Accounts Manager'];

// Constants
const MAX_UPLOAD_SIZE_MB = 20;
const MAX_UPLOAD_COUNT = 10;
const ALLOWED_MEDIA_TYPES_PREFIXES = ['image/', 'video/'];
const META_PLATFORMS = ['Facebook', 'Instagram'];

const STATUS_BADGE_SUFFIX = {
  'New Request': 'NewRequest',
  'Content Writing': 'InProgress',
  'Content Ready': 'Ready',
  'Design / Editing': 'InProgress',
  'Design Ready': 'Ready',
  'Internal Review': 'Review',
  'Changes Requested': 'ChangesRequested',
  'Ready for Client Approval': 'Review',
  'Client Approval': 'Review',
  'Approved': 'Approved',
  'Scheduled': 'Scheduled',
  'Published': 'Published',
  'Cancelled': 'Cancelled',
};

const getContentStatusClassSuffix = (status) => STATUS_BADGE_SUFFIX[status] || 'Default';

const TRANSLATIONS = {
  en: {
    loadingDetails: 'Loading content task details...',
    contentTaskUnavailable: 'Content task data unavailable.',
    taskIdMissing: 'Content Task ID missing.',
    taskNotFound: 'Content task not found.',
    accessDeniedMsg: 'Access Denied: You do not have permission to view this content task.',
    loadFailedRealtime: 'Failed to load content task details in real-time. Please refresh.',
    commentsLoadFailed: 'Failed to load comments.',
    activityLoadFailed: 'Failed to load activity.',
    untitledTask: 'Untitled Content Task',
    edit: 'Edit',
    cancelTask: 'Cancel Task',
    backToList: 'Back to List',
    status: 'Status:',
    client: 'Client:',
    page: 'Page:',
    platform: 'Platforms & Pages:',
    contentType: 'Content Type:',
    dueDate: 'Due Date:',
    scheduledDate: 'Scheduled Date:',
    publishedDate: 'Published Date:',
    postUrl: 'Post URLs:',
    publishedTag: 'Published',
    caption: 'Caption:',
    hashtags: 'Hashtags:',
    cta: 'Call To Action:',
    writer: 'Writer:',
    designer: 'Designer:',
    reviewer: 'Reviewer:',
    assignedTo: 'Assigned To:',
    clientApproval: 'Client Approval:',
    clientNotes: 'Client Notes:',
    internalNotes: 'Internal Notes:',
    notLinked: 'Not linked',
    na: 'N/A',
    unassigned: 'Unassigned',
    none: 'None',
    createdBy: 'Created By:',
    created: 'Created:',
    lastUpdated: 'Last Updated:',
    workflowActions: 'Workflow Actions',
    startContentWriting: 'Start Content Writing',
    markContentReady: 'Mark Content Ready',
    sendToDesignEditing: 'Send to Design / Editing',
    skipDesignSendToInternalReview: 'Skip Design — Send to Internal Review',
    markDesignReady: 'Mark Design Ready',
    sendToInternalReview: 'Send to Internal Review',
    requestChanges: 'Request Changes',
    approveReadyForClient: 'Approve — Ready for Client',
    resumeContentWriting: 'Resume: Content Writing',
    resumeDesignEditing: 'Resume: Design / Editing',
    sendToClientAwaitingApproval: 'Send to Client (Awaiting Approval)',
    clientRejected: 'Client Rejected',
    clientApproved: 'Client Approved',
    schedulePost: 'Schedule Post',
    markAsPublished: 'Mark as Published',
    publishedLockedMsg: 'This content has been published.',
    publishRemainingMeta: 'Retry remaining platforms',
    publishRemainingHint: (platforms) => `Published, but not yet on: ${platforms}. You can retry just those.`,
    cancelledLockedMsg: 'This content task was cancelled.',
    changesNoteLabel: 'Note explaining requested changes (required)',
    changesNotePlaceholder: 'Explain what needs to change...',
    clientRejectNoteLabel: 'Client feedback / rejection reason (required)',
    clientRejectNotePlaceholder: 'What did the client ask to change?',
    scheduledDateTimeLabel: 'Scheduled date & time (required)',
    postUrlLabel: 'Live Post URL (required)',
    postUrlPlaceholder: 'https://...',
    cancel: 'Cancel',
    submit: 'Submit',
    confirmSchedule: 'Confirm Schedule',
    confirmPublished: 'Confirm Published',
    mediaSectionTitle: (count, max) => `Media (${count}/${max})`,
    uploadImageVideo: (max, sizeMb) => `Upload Image/Video (Max ${max} files, ${sizeMb}MB each)`,
    uploading: 'Uploading...',
    removeMedia: 'Remove media',
    noMediaUploaded: 'No media uploaded yet.',
    commentsSectionTitle: (count) => `Comments (${count})`,
    noCommentsYet: 'No comments yet.',
    loadingComments: 'Loading comments...',
    addCommentPlaceholder: 'Add a comment...',
    addComment: 'Add Comment',
    activityLogTitle: 'Activity Log',
    loadingActivity: 'Loading activity...',
    noActivityRecorded: 'No activity recorded yet.',
    userFallback: 'User',
    systemFallback: 'System',
    unknownUser: 'Unknown',
    clientApprovalStatusLabels: { Pending: 'Pending', Approved: 'Approved', Rejected: 'Rejected' },
    // Confirms / errors / success
    confirmCancelTask: (title) => `Cancel content task "${title}"? This cannot be undone via the workflow.`,
    confirmRemoveMedia: (name) => `Remove "${name}" from this content task?`,
    errChangesNoteRequired: 'Please provide a note explaining the requested changes.',
    errClientRejectNoteRequired: 'Please provide the client feedback / rejection reason.',
    errScheduledDateRequired: 'Please pick a scheduled date/time.',
    errInvalidScheduledDate: 'Invalid scheduled date.',
    errPostUrlRequired: 'Please paste the live post URL.',
    errPermissionDeniedCancel: 'Permission denied to cancel this content task.',
    errUploadLimit: (remaining, max) => `You can only upload ${remaining} more file(s) (Max ${max} total).`,
    errOnlyImageVideo: 'Only image and video files are allowed.',
    errFilesTooLarge: (maxMb) => `Files larger than ${maxMb}MB were skipped.`,
    errUploadFailed: 'Failed to upload media. Please try again.',
    errRemoveMediaFailed: 'Failed to remove media.',
    errCommentEmpty: 'Comment cannot be empty.',
    errCommentFailed: 'Failed to add comment. Please try again.',
    statusUpdated: (status, language) => `Status updated to "${getStatusLabel(status, language)}".`,
    statusUpdateFailed: (status, language) => `Failed to update status to "${getStatusLabel(status, language)}". Please try again.`,
    mediaUploadedSuccess: 'Media uploaded successfully.',
    mediaRemovedSuccess: 'Media removed.',
    publishNowMeta: 'Publish Now via Meta API',
    publishingMeta: 'Publishing...',
    metaPublishFailed: 'Failed to publish via Meta API. Please try again or check the page connection.',
    metaPublishSuccess: 'Successfully published via Meta API!',
    metaPublishAlready: 'This task has already been published to all of its linked platforms.',
    metaPublishPartial: (failed) => `Published to the connected platforms, but failed for: ${failed}`,
    metaLastErrorLabel: 'Last automatic publish attempt failed:',
  },
  ar: {
    loadingDetails: 'جارٍ تحميل تفاصيل مهمة المحتوى...',
    contentTaskUnavailable: 'بيانات مهمة المحتوى غير متوفرة.',
    taskIdMissing: 'معرّف مهمة المحتوى غير موجود.',
    taskNotFound: 'مهمة المحتوى غير موجودة.',
    accessDeniedMsg: 'تم رفض الوصول: لا تملك صلاحية عرض مهمة المحتوى هذه.',
    loadFailedRealtime: 'فشل تحميل تفاصيل مهمة المحتوى في الوقت الفعلي. يرجى تحديث الصفحة.',
    commentsLoadFailed: 'فشل تحميل التعليقات.',
    activityLoadFailed: 'فشل تحميل النشاط.',
    untitledTask: 'مهمة محتوى بدون عنوان',
    edit: 'تعديل',
    cancelTask: 'إلغاء المهمة',
    backToList: 'العودة إلى القائمة',
    status: 'الحالة:',
    client: 'العميل:',
    page: 'الصفحة:',
    platform: 'المنصات والصفحات:',
    contentType: 'نوع المحتوى:',
    dueDate: 'تاريخ الاستحقاق:',
    scheduledDate: 'تاريخ الجدولة:',
    publishedDate: 'تاريخ النشر:',
    postUrl: 'روابط المنشورات:',
    publishedTag: 'تم النشر',
    caption: 'النص المصاحب:',
    hashtags: 'الوسوم:',
    cta: 'دعوة لاتخاذ إجراء:',
    writer: 'الكاتب:',
    designer: 'المصمم:',
    reviewer: 'المراجع:',
    assignedTo: 'معيّن إلى:',
    clientApproval: 'موافقة العميل:',
    clientNotes: 'ملاحظات العميل:',
    internalNotes: 'ملاحظات داخلية:',
    notLinked: 'غير مرتبط',
    na: 'غير متوفر',
    unassigned: 'غير معيّن',
    none: 'لا يوجد',
    createdBy: 'أنشأه:',
    created: 'تاريخ الإنشاء:',
    lastUpdated: 'آخر تحديث:',
    workflowActions: 'إجراءات سير العمل',
    startContentWriting: 'بدء كتابة المحتوى',
    markContentReady: 'تحديد المحتوى كجاهز',
    sendToDesignEditing: 'إرسال إلى التصميم / التعديل',
    skipDesignSendToInternalReview: 'تجاوز التصميم — إرسال إلى المراجعة الداخلية',
    markDesignReady: 'تحديد التصميم كجاهز',
    sendToInternalReview: 'إرسال إلى المراجعة الداخلية',
    requestChanges: 'طلب تعديلات',
    approveReadyForClient: 'الموافقة — جاهز للعميل',
    resumeContentWriting: 'استئناف: كتابة المحتوى',
    resumeDesignEditing: 'استئناف: التصميم / التعديل',
    sendToClientAwaitingApproval: 'إرسال إلى العميل (في انتظار الموافقة)',
    clientRejected: 'رفض العميل',
    clientApproved: 'موافقة العميل',
    schedulePost: 'جدولة المنشور',
    markAsPublished: 'تحديد كمنشور',
    publishedLockedMsg: 'تم نشر هذا المحتوى.',
    publishRemainingMeta: 'إعادة المحاولة للمنصات المتبقية',
    publishRemainingHint: (platforms) => `تم النشر، ولكن ليس بعد على: ${platforms}. يمكنك إعادة المحاولة لهذه المنصات فقط.`,
    cancelledLockedMsg: 'تم إلغاء مهمة المحتوى هذه.',
    changesNoteLabel: 'ملاحظة توضح التعديلات المطلوبة (مطلوب)',
    changesNotePlaceholder: 'وضّح ما الذي يجب تغييره...',
    clientRejectNoteLabel: 'ملاحظات العميل / سبب الرفض (مطلوب)',
    clientRejectNotePlaceholder: 'ما الذي طلب العميل تغييره؟',
    scheduledDateTimeLabel: 'تاريخ ووقت الجدولة (مطلوب)',
    postUrlLabel: 'رابط المنشور المباشر (مطلوب)',
    postUrlPlaceholder: 'https://...',
    cancel: 'إلغاء',
    submit: 'إرسال',
    confirmSchedule: 'تأكيد الجدولة',
    confirmPublished: 'تأكيد النشر',
    mediaSectionTitle: (count, max) => `الوسائط (${count}/${max})`,
    uploadImageVideo: (max, sizeMb) => `تحميل صورة/فيديو (حد أقصى ${max} ملفات، ${sizeMb}م.ب لكل ملف)`,
    uploading: 'جارٍ التحميل...',
    removeMedia: 'إزالة الوسائط',
    noMediaUploaded: 'لم يتم تحميل أي وسائط بعد.',
    commentsSectionTitle: (count) => `التعليقات (${count})`,
    noCommentsYet: 'لا توجد تعليقات بعد.',
    loadingComments: 'جارٍ تحميل التعليقات...',
    addCommentPlaceholder: 'أضف تعليقاً...',
    addComment: 'إضافة تعليق',
    activityLogTitle: 'سجل النشاط',
    loadingActivity: 'جارٍ تحميل النشاط...',
    noActivityRecorded: 'لا يوجد نشاط مسجل بعد.',
    userFallback: 'مستخدم',
    systemFallback: 'النظام',
    unknownUser: 'غير معروف',
    clientApprovalStatusLabels: { Pending: 'قيد الانتظار', Approved: 'تمت الموافقة', Rejected: 'مرفوض' },
    confirmCancelTask: (title) => `إلغاء مهمة المحتوى "${title}"؟ لا يمكن التراجع عن هذا عبر سير العمل.`,
    confirmRemoveMedia: (name) => `إزالة "${name}" من مهمة المحتوى هذه؟`,
    errChangesNoteRequired: 'يرجى تقديم ملاحظة توضح التعديلات المطلوبة.',
    errClientRejectNoteRequired: 'يرجى تقديم ملاحظات العميل / سبب الرفض.',
    errScheduledDateRequired: 'يرجى اختيار تاريخ ووقت الجدولة.',
    errInvalidScheduledDate: 'تاريخ الجدولة غير صالح.',
    errPostUrlRequired: 'يرجى لصق رابط المنشور المباشر.',
    errPermissionDeniedCancel: 'تم رفض إذن إلغاء مهمة المحتوى هذه.',
    errUploadLimit: (remaining, max) => `يمكنك تحميل ${remaining} ملف(ات) إضافية فقط (الحد الأقصى ${max} ملفاً إجمالاً).`,
    errOnlyImageVideo: 'يُسمح فقط بملفات الصور والفيديو.',
    errFilesTooLarge: (maxMb) => `تم تجاوز الملفات الأكبر من ${maxMb}م.ب.`,
    errUploadFailed: 'فشل تحميل الوسائط. يرجى المحاولة مرة أخرى.',
    errRemoveMediaFailed: 'فشلت إزالة الوسائط.',
    errCommentEmpty: 'لا يمكن أن يكون التعليق فارغاً.',
    errCommentFailed: 'فشل إضافة التعليق. يرجى المحاولة مرة أخرى.',
    statusUpdated: (status, language) => `تم تحديث الحالة إلى "${getStatusLabel(status, language)}".`,
    statusUpdateFailed: (status, language) => `فشل تحديث الحالة إلى "${getStatusLabel(status, language)}". يرجى المحاولة مرة أخرى.`,
    mediaUploadedSuccess: 'تم تحميل الوسائط بنجاح.',
    mediaRemovedSuccess: 'تمت إزالة الوسائط.',
    publishNowMeta: 'النشر الآن عبر Meta API',
    publishingMeta: 'جارٍ النشر...',
    metaPublishFailed: 'فشل النشر عبر Meta API. يرجى المحاولة مرة أخرى أو التحقق من اتصال الصفحة.',
    metaPublishSuccess: 'تم النشر بنجاح عبر Meta API!',
    metaPublishAlready: 'تم نشر هذه المهمة مسبقاً على جميع منصاتها المرتبطة.',
    metaPublishPartial: (failed) => `تم النشر على المنصات المتصلة، ولكن فشل النشر على: ${failed}`,
    metaLastErrorLabel: 'فشلت آخر محاولة نشر تلقائي:',
  },
};

const ContentTaskDetailPage = () => {
  const { taskId } = useParams();
  const { currentUser, userRole } = useAuth();
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = useMemo(() => TRANSLATIONS[language], [language]);

  // --- Component State ---
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const [, setAllUsers] = useState([]); // for resolving uid -> name
  const usersMapRef = useRef(new Map());

  // Comments
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentError, setCommentError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  // Activity log
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState('');

  // Action-loading flags
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDeletingMediaPath, setIsDeletingMediaPath] = useState(null);

  // Inline forms / modals
  const [showChangesRequestedForm, setShowChangesRequestedForm] = useState(false);
  const [changesNote, setChangesNote] = useState('');
  const [showClientRejectForm, setShowClientRejectForm] = useState(false);
  const [clientRejectNote, setClientRejectNote] = useState('');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduledDateInput, setScheduledDateInput] = useState('');
  const [showPublishForm, setShowPublishForm] = useState(false);
  const [postUrlInput, setPostUrlInput] = useState('');

  // Meta (Facebook/Instagram) publishing — keyed by pageId, since a task can target
  // more than one platform/page at once.
  const [linkedPagesInfo, setLinkedPagesInfo] = useState({});
  const [isPublishingMeta, setIsPublishingMeta] = useState(false);

  const isMounted = useRef(true);
  const successTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const isProcessingAction = isTransitioning || isUploadingFile || !!isDeletingMediaPath || isAddingComment || isPublishingMeta;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const showSuccess = useCallback((msg, ms = 3500) => {
    if (!isMounted.current) return;
    setSuccessMessage(msg);
    setError('');
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setSuccessMessage(''); }, ms);
  }, []);

  const showError = useCallback((msg, ms = 4000) => {
    if (!isMounted.current) return;
    setError(msg);
    setSuccessMessage('');
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, ms);
  }, []);

  // Fetch all users once (for resolving uid -> name)
  useEffect(() => {
    const fetchAllUsers = async () => {
      if (!isMounted.current) return;
      try {
        const usersCol = collection(db, 'users');
        const q = query(usersCol, orderBy('name', 'asc'));
        const snapshot = await getDocs(q);
        if (!isMounted.current) return;
        const userList = snapshot.docs.map(d => ({
          id: d.id,
          name: d.data().name || d.data().email || d.id,
          email: d.data().email || '',
          role: d.data().role || '',
          photoURL: d.data().photoURL || null,
        }));
        setAllUsers(userList);
        const m = new Map();
        userList.forEach(u => m.set(u.id, u));
        usersMapRef.current = m;
      } catch (err) {
        console.error('Error fetching users for content task detail:', err);
      }
    };
    fetchAllUsers();
  }, []);

  const findUserNameById = useCallback((userId) => {
    if (!userId) return t.unknownUser;
    const u = usersMapRef.current.get(userId);
    return u?.name || u?.email || String(userId).substring(0, 6);
  }, [t.unknownUser]);

  // A task can target more than one platform/page at once. `task.targets` is the source of
  // truth; older single-platform tasks only have the singular platform/pageId/pageName fields.
  const taskTargets = useMemo(() => {
    if (!task) return [];
    if (Array.isArray(task.targets) && task.targets.length > 0) return task.targets;
    if (task.platform) return [{ platform: task.platform, pageId: task.pageId || '', pageName: task.pageName || '' }];
    return [];
  }, [task]);

  const targetPageIdsKey = useMemo(
    () => Array.from(new Set(taskTargets.map(tg => tg.pageId).filter(Boolean))).sort().join(','),
    [taskTargets]
  );

  // Fetch the linked social pages (to check each one's Meta connection status)
  useEffect(() => {
    const pageIds = targetPageIdsKey ? targetPageIdsKey.split(',') : [];
    if (pageIds.length === 0) { setLinkedPagesInfo({}); return; }
    const fetchLinkedPages = async () => {
      try {
        const entries = await Promise.all(pageIds.map(async (pid) => {
          const pageSnap = await getDoc(doc(db, 'socialPages', pid));
          return [pid, pageSnap.exists() ? pageSnap.data() : null];
        }));
        if (!isMounted.current) return;
        setLinkedPagesInfo(Object.fromEntries(entries));
      } catch (err) {
        console.error('Error fetching linked social pages:', err);
        if (isMounted.current) setLinkedPagesInfo({});
      }
    };
    fetchLinkedPages();
  }, [targetPageIdsKey]);

  // Real-time content task listener
  useEffect(() => {
    if (!taskId) { setError(t.taskIdMissing); setLoading(false); return; }
    if (!currentUser) return;
    if (!userRole) { setLoading(true); return; }

    if (isMounted.current) {
      setLoading(true);
      setError('');
      setAccessDenied(false);
      setTask(null);
    }

    const taskRef = doc(db, 'contentTasks', taskId);
    const unsubscribe = onSnapshot(taskRef, (snap) => {
      if (!isMounted.current) return;
      if (!snap.exists()) {
        setError(t.taskNotFound);
        setTask(null);
        setLoading(false);
        return;
      }

      const data = snap.data();
      const taskData = {
        id: snap.id,
        ...data,
        dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : null,
        scheduledDate: data.scheduledDate?.toDate ? data.scheduledDate.toDate() : null,
        publishedDate: data.publishedDate?.toDate ? data.publishedDate.toDate() : null,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : null,
      };

      const isManager = managerRoles.includes(userRole);
      const isCreator = taskData.createdBy === currentUser.uid;
      const isAssigned = (taskData.assignedTo || []).includes(currentUser.uid);
      const isWriterDesignerReviewer = [taskData.writerId, taskData.designerId, taskData.reviewerId].includes(currentUser.uid);
      const isVisible = (taskData.visibleTo || []).includes(currentUser.uid);
      const canView = viewRoles.includes(userRole) && (isManager || isCreator || isAssigned || isWriterDesignerReviewer || isVisible);

      if (!canView) {
        setAccessDenied(true);
        setError(t.accessDeniedMsg);
        setTask(null);
        setLoading(false);
        return;
      }

      setTask(taskData);
      setLoading(false);
    }, (err) => {
      if (!isMounted.current) return;
      console.error('Error in content task snapshot listener:', err);
      setError(t.loadFailedRealtime);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [taskId, currentUser, userRole, t]);

  // Comments stream
  useEffect(() => {
    if (!taskId) return;
    setCommentsLoading(true);
    setCommentError('');
    const commentsRef = collection(db, 'contentTasks', taskId, 'comments');
    const q = query(commentsRef, orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      if (!isMounted.current) return;
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data(), timestamp: d.data().timestamp?.toDate() })));
      setCommentsLoading(false);
    }, (err) => {
      if (!isMounted.current) return;
      console.error('Error fetching content task comments:', err);
      setCommentError(t.commentsLoadFailed);
      setCommentsLoading(false);
    });
    return () => unsubscribe();
  }, [taskId, t]);

  // Activity log stream
  useEffect(() => {
    if (!taskId) return;
    setActivityLoading(true);
    setActivityError('');
    const activityRef = collection(db, 'contentTasks', taskId, 'activityLog');
    const q = query(activityRef, orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      if (!isMounted.current) return;
      setActivityLog(snap.docs.map(d => ({ id: d.id, ...d.data(), timestamp: d.data().timestamp?.toDate() })));
      setActivityLoading(false);
    }, (err) => {
      if (!isMounted.current) return;
      console.error('Error fetching content task activity log:', err);
      setActivityError(t.activityLoadFailed);
      setActivityLoading(false);
    });
    return () => unsubscribe();
  }, [taskId, t]);

  // --- Helpers shared by transitions ---
  const buildUserIdsToNotify = useCallback((extraUids = []) => {
    const ids = new Set();
    if (!task) return ids;
    if (task.createdBy && task.createdBy !== currentUser?.uid) ids.add(task.createdBy);
    (task.assignedTo || []).forEach(uid => { if (uid !== currentUser?.uid) ids.add(uid); });
    [task.writerId, task.designerId, task.reviewerId].forEach(uid => {
      if (uid && uid !== currentUser?.uid) ids.add(uid);
    });
    extraUids.forEach(uid => { if (uid && uid !== currentUser?.uid) ids.add(uid); });
    return ids;
  }, [task, currentUser]);

  const addNotificationsToBatch = useCallback((batch, userIds, message, type) => {
    if (!userIds || userIds.size === 0) return;
    const notificationsCol = collection(db, 'notifications');
    const creatorName = currentUser?.displayName || currentUser?.email || 'Someone';
    userIds.forEach(uid => {
      const notifRef = doc(notificationsCol);
      batch.set(notifRef, {
        userId: uid,
        contentTaskId: taskId,
        taskName: task?.title || 'Untitled Content Task',
        message,
        timestamp: serverTimestamp(),
        isRead: false,
        createdBy: currentUser?.uid || null,
        createdByName: creatorName,
        type,
      });
    });
  }, [currentUser, task, taskId]);

  const performTransition = useCallback(async ({
    nextStatus, extraUpdates = {}, activityType, activityDescription,
    changes = [], notifyExtraUids = [], notifyMessage, notifyType,
    addCommentText = null,
  }) => {
    if (!task || !currentUser || isProcessingAction) return;
    setIsTransitioning(true);
    setError('');

    try {
      const taskRef = doc(db, 'contentTasks', taskId);
      const batch = writeBatch(db);
      const actorName = currentUser.displayName || currentUser.email || 'Someone';

      batch.update(taskRef, {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        ...extraUpdates,
      });

      const activityLogRef = doc(collection(db, 'contentTasks', taskId, 'activityLog'));
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: actorName,
        actionType: activityType,
        description: activityDescription,
        changes,
      });

      if (addCommentText && addCommentText.trim()) {
        const commentRef = doc(collection(db, 'contentTasks', taskId, 'comments'));
        batch.set(commentRef, {
          timestamp: serverTimestamp(),
          userId: currentUser.uid,
          userName: actorName,
          text: addCommentText.trim(),
        });
      }

      const userIdsToNotify = buildUserIdsToNotify(notifyExtraUids);
      addNotificationsToBatch(batch, userIdsToNotify, notifyMessage, notifyType);

      await batch.commit();
      showSuccess(t.statusUpdated(nextStatus, language));
      return true;
    } catch (err) {
      console.error(`Error transitioning content task to ${nextStatus}:`, err);
      showError(t.statusUpdateFailed(nextStatus, language));
      return false;
    } finally {
      if (isMounted.current) setIsTransitioning(false);
    }
  }, [task, currentUser, taskId, isProcessingAction, buildUserIdsToNotify, addNotificationsToBatch, showSuccess, showError, t, language]);

  // --- Permission checks for transitions ---
  const isCurrentUserCreator = task?.createdBy === currentUser?.uid;
  const isCurrentUserAssigned = (task?.assignedTo || []).includes(currentUser?.uid);
  const isCurrentUserWriter = task?.writerId === currentUser?.uid;
  const isCurrentUserDesigner = task?.designerId === currentUser?.uid;
  const isCurrentUserReviewer = task?.reviewerId === currentUser?.uid;
  const isManagerUser = managerRoles.includes(userRole);
  const canActOnTask = isManagerUser || isCurrentUserCreator || isCurrentUserAssigned || isCurrentUserWriter || isCurrentUserDesigner || isCurrentUserReviewer;

  // --- Transition handlers ---
  const handleStartWriting = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Content Writing',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} started content writing.`,
      changes: [{ field: 'Status', from: task.status, to: 'Content Writing' }],
      notifyMessage: `Content writing started for "${task.title}".`,
      notifyType: 'content_task_status',
    });
  };

  const handleMarkContentReady = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Content Ready',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} marked content/copy as ready.`,
      changes: [{ field: 'Status', from: task.status, to: 'Content Ready' }],
      notifyMessage: `Content is ready for "${task.title}".`,
      notifyType: 'content_task_status',
    });
  };

  const handleSendToDesign = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Design / Editing',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} sent content to Design / Editing.`,
      changes: [{ field: 'Status', from: task.status, to: 'Design / Editing' }],
      notifyMessage: `"${task.title}" is now in Design / Editing.`,
      notifyType: 'content_task_status',
    });
  };

  const handleSkipToInternalReview = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Internal Review',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} sent content directly to Internal Review (design skipped).`,
      changes: [{ field: 'Status', from: task.status, to: 'Internal Review' }],
      notifyMessage: `"${task.title}" is ready for Internal Review.`,
      notifyType: 'content_task_status',
    });
  };

  const handleMarkDesignReady = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Design Ready',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} marked design/editing as ready.`,
      changes: [{ field: 'Status', from: task.status, to: 'Design Ready' }],
      notifyMessage: `Design is ready for "${task.title}".`,
      notifyType: 'content_task_status',
    });
  };

  const handleSendToInternalReview = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Internal Review',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} sent content to Internal Review.`,
      changes: [{ field: 'Status', from: task.status, to: 'Internal Review' }],
      notifyMessage: `"${task.title}" is ready for Internal Review.`,
      notifyType: 'content_task_status',
    });
  };

  const handleApproveInternally = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Ready for Client Approval',
      activityType: 'internal_review_approved',
      activityDescription: `${currentUser.displayName || currentUser.email} approved internally. Ready for client approval.`,
      changes: [{ field: 'Status', from: task.status, to: 'Ready for Client Approval' }],
      notifyMessage: `"${task.title}" passed internal review and is ready for client approval.`,
      notifyType: 'content_task_status',
    });
  };

  const openChangesRequestedForm = () => {
    setChangesNote('');
    setShowChangesRequestedForm(true);
  };

  const submitChangesRequested = async () => {
    if (!task) return;
    if (!changesNote.trim()) { showError(t.errChangesNoteRequired); return; }
    const ok = await performTransition({
      nextStatus: 'Changes Requested',
      extraUpdates: { internalNotes: changesNote.trim() },
      activityType: 'internal_review_rejected',
      activityDescription: `${currentUser.displayName || currentUser.email} requested changes after internal review. Note: ${changesNote.trim()}`,
      changes: [{ field: 'Status', from: task.status, to: 'Changes Requested' }],
      notifyMessage: `Changes requested for "${task.title}": ${changesNote.trim()}`,
      notifyType: 'content_task_status',
      addCommentText: `Changes Requested (Internal Review): ${changesNote.trim()}`,
    });
    if (ok) setShowChangesRequestedForm(false);
  };

  const handleResumeWriting = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Content Writing',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} resumed content writing to address requested changes.`,
      changes: [{ field: 'Status', from: task.status, to: 'Content Writing' }],
      notifyMessage: `"${task.title}" is back in Content Writing to address changes.`,
      notifyType: 'content_task_status',
    });
  };

  const handleResumeDesign = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Design / Editing',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} resumed Design / Editing to address requested changes.`,
      changes: [{ field: 'Status', from: task.status, to: 'Design / Editing' }],
      notifyMessage: `"${task.title}" is back in Design / Editing to address changes.`,
      notifyType: 'content_task_status',
    });
  };

  const handleSendToClient = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Client Approval',
      extraUpdates: { clientApprovalStatus: 'Pending' },
      activityType: 'sent_to_client',
      activityDescription: `${currentUser.displayName || currentUser.email} sent content to client for approval.`,
      changes: [{ field: 'Status', from: task.status, to: 'Client Approval' }],
      notifyMessage: `"${task.title}" was sent to the client for approval.`,
      notifyType: 'content_task_status',
    });
  };

  const handleClientApprove = () => {
    if (!task) return;
    performTransition({
      nextStatus: 'Approved',
      extraUpdates: { clientApprovalStatus: 'Approved' },
      activityType: 'client_approved',
      activityDescription: `${currentUser.displayName || currentUser.email} recorded client approval.`,
      changes: [{ field: 'Status', from: task.status, to: 'Approved' }],
      notifyMessage: `Client approved "${task.title}"!`,
      notifyType: 'content_task_status',
    });
  };

  const openClientRejectForm = () => {
    setClientRejectNote('');
    setShowClientRejectForm(true);
  };

  const submitClientReject = async () => {
    if (!task) return;
    if (!clientRejectNote.trim()) { showError(t.errClientRejectNoteRequired); return; }
    const ok = await performTransition({
      nextStatus: 'Changes Requested',
      extraUpdates: { clientApprovalStatus: 'Rejected', clientNotes: clientRejectNote.trim() },
      activityType: 'client_rejected',
      activityDescription: `${currentUser.displayName || currentUser.email} recorded client rejection. Notes: ${clientRejectNote.trim()}`,
      changes: [{ field: 'Status', from: task.status, to: 'Changes Requested' }],
      notifyMessage: `Client requested changes for "${task.title}": ${clientRejectNote.trim()}`,
      notifyType: 'content_task_status',
      addCommentText: `Client Feedback (Rejected): ${clientRejectNote.trim()}`,
    });
    if (ok) setShowClientRejectForm(false);
  };

  const openScheduleForm = () => {
    setScheduledDateInput('');
    setShowScheduleForm(true);
  };

  const submitSchedule = async () => {
    if (!task) return;
    if (!scheduledDateInput) { showError(t.errScheduledDateRequired); return; }
    const parsed = new Date(scheduledDateInput);
    if (isNaN(parsed.getTime())) { showError(t.errInvalidScheduledDate); return; }

    const ok = await performTransition({
      nextStatus: 'Scheduled',
      extraUpdates: { scheduledDate: Timestamp.fromDate(parsed) },
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} scheduled the post for ${formatDate(parsed, { dateStyle: 'medium', timeStyle: 'short' })}.`,
      changes: [{ field: 'Status', from: task.status, to: 'Scheduled' }, { field: 'Scheduled Date', to: parsed.toString() }],
      notifyMessage: `"${task.title}" was scheduled for ${formatDate(parsed, { dateStyle: 'medium', timeStyle: 'short' })}.`,
      notifyType: 'content_task_status',
    });
    if (ok) setShowScheduleForm(false);
  };

  const openPublishForm = () => {
    setPostUrlInput(task?.postUrl || '');
    setShowPublishForm(true);
  };

  const submitPublish = async () => {
    if (!task) return;
    if (!postUrlInput.trim()) { showError(t.errPostUrlRequired); return; }
    const ok = await performTransition({
      nextStatus: 'Published',
      extraUpdates: { postUrl: postUrlInput.trim(), publishedDate: serverTimestamp() },
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} marked content as Published.`,
      changes: [{ field: 'Status', from: task.status, to: 'Published' }, { field: 'Post URL', to: postUrlInput.trim() }],
      notifyMessage: `"${task.title}" has been published!`,
      notifyType: 'content_task_status',
    });
    if (ok) setShowPublishForm(false);
  };

  const canPublishViaMeta = !!task && taskTargets.some(
    (tg) => META_PLATFORMS.includes(tg.platform) && !!tg.pageId && linkedPagesInfo[tg.pageId]?.metaConnected === true
  );

  // Connected Meta targets that haven't been published yet (e.g. one platform failed while
  // others succeeded) — these can be retried even after the task is marked Published.
  const pendingMetaTargets = !task ? [] : taskTargets.filter(
    (tg) => META_PLATFORMS.includes(tg.platform) && !!tg.pageId
      && linkedPagesInfo[tg.pageId]?.metaConnected === true
      && !(task.postUrls || []).some((p) => p.pageId === tg.pageId)
  );

  const handlePublishNowMeta = async () => {
    if (!task || isProcessingAction) return;
    setIsPublishingMeta(true);
    setError('');
    try {
      const publishContentTaskNow = httpsCallable(agencyFunctions, 'publishContentTaskNow');
      const res = await publishContentTaskNow({ taskId });
      const resultData = res.data || {};
      if (resultData.alreadyPublished) {
        showSuccess(t.metaPublishAlready);
      } else if (Array.isArray(resultData.failedPlatforms) && resultData.failedPlatforms.length > 0) {
        const failedSummary = resultData.failedPlatforms
          .map((f) => `${getPlatformLabel(f.platform, language)} (${f.message})`)
          .join('; ');
        showError(t.metaPublishPartial(failedSummary));
      } else {
        showSuccess(t.metaPublishSuccess);
      }
    } catch (err) {
      console.error('Error publishing via Meta API:', err);
      showError(err.message || t.metaPublishFailed);
    } finally {
      if (isMounted.current) setIsPublishingMeta(false);
    }
  };

  const handleCancel = () => {
    if (!task) return;
    if (!cancelRoles.includes(userRole)) { showError(t.errPermissionDeniedCancel); return; }
    if (!window.confirm(t.confirmCancelTask(task.title))) return;
    performTransition({
      nextStatus: 'Cancelled',
      activityType: 'status_changed',
      activityDescription: `${currentUser.displayName || currentUser.email} cancelled the content task.`,
      changes: [{ field: 'Status', from: task.status, to: 'Cancelled' }],
      notifyMessage: `"${task.title}" was cancelled.`,
      notifyType: 'content_task_status',
    });
  };

  // --- Media Upload ---
  const handleFileChange = async (e) => {
    setError('');
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (!task) return;
    const currentCount = (task.mediaUrls || []).length;
    const remainingSlots = MAX_UPLOAD_COUNT - currentCount;

    if (files.length > remainingSlots) {
      showError(t.errUploadLimit(remainingSlots, MAX_UPLOAD_COUNT));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const validFiles = [];
    let typeError = false;
    let sizeError = false;
    files.forEach(file => {
      const isAllowedType = ALLOWED_MEDIA_TYPES_PREFIXES.some(prefix => file.type.startsWith(prefix));
      if (!isAllowedType) { typeError = true; return; }
      if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) { sizeError = true; return; }
      validFiles.push(file);
    });

    const errMsgs = [];
    if (typeError) errMsgs.push(t.errOnlyImageVideo);
    if (sizeError) errMsgs.push(t.errFilesTooLarge(MAX_UPLOAD_SIZE_MB));
    if (errMsgs.length > 0) showError(errMsgs.join(' '));

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploadingFile(true);
    try {
      const uploadPromises = validFiles.map(file => {
        const filePath = `contentMedia/${taskId}/${Date.now()}_${file.name}`;
        const fileStorageRef = ref(storage, filePath);
        return uploadBytes(fileStorageRef, file).then(uploadResult =>
          getDownloadURL(uploadResult.ref).then(url => ({
            url, name: file.name, type: file.type, size: file.size, path: filePath
          }))
        );
      });
      const uploadedFileData = await Promise.all(uploadPromises);

      const taskRef = doc(db, 'contentTasks', taskId);
      const batch = writeBatch(db);
      batch.update(taskRef, {
        mediaUrls: arrayUnion(...uploadedFileData),
        updatedAt: serverTimestamp(),
      });

      const activityLogRef = doc(collection(db, 'contentTasks', taskId, 'activityLog'));
      const actorName = currentUser.displayName || currentUser.email || 'Someone';
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: actorName,
        actionType: 'media_uploaded',
        description: `${actorName} uploaded ${uploadedFileData.length} media file(s).`,
      });

      await batch.commit();
      showSuccess(t.mediaUploadedSuccess);
    } catch (err) {
      console.error('Error uploading media:', err);
      showError(t.errUploadFailed);
    } finally {
      if (isMounted.current) setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteMedia = async (mediaItem) => {
    if (!task || isProcessingAction) return;
    if (!window.confirm(t.confirmRemoveMedia(mediaItem.name))) return;
    setIsDeletingMediaPath(mediaItem.path);
    try {
      const remaining = (task.mediaUrls || []).filter(m => m.path !== mediaItem.path);
      const taskRef = doc(db, 'contentTasks', taskId);
      const batch = writeBatch(db);
      batch.update(taskRef, { mediaUrls: remaining, updatedAt: serverTimestamp() });

      const activityLogRef = doc(collection(db, 'contentTasks', taskId, 'activityLog'));
      const actorName = currentUser.displayName || currentUser.email || 'Someone';
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: actorName,
        actionType: 'media_deleted',
        description: `${actorName} removed media file "${mediaItem.name}".`,
      });

      await batch.commit();

      if (mediaItem.path) {
        try { await deleteObject(ref(storage, mediaItem.path)); } catch (storageErr) {
          if (storageErr.code !== 'storage/object-not-found') console.error('Error deleting storage object:', storageErr);
        }
      }

      showSuccess(t.mediaRemovedSuccess);
    } catch (err) {
      console.error('Error deleting media:', err);
      showError(t.errRemoveMediaFailed);
    } finally {
      if (isMounted.current) setIsDeletingMediaPath(null);
    }
  };

  // --- Comments ---
  const handleAddComment = async (e) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text) { setCommentError(t.errCommentEmpty); return; }
    if (!currentUser || !task || isProcessingAction) return;

    setIsAddingComment(true);
    setCommentError('');

    try {
      const batch = writeBatch(db);
      const commentRef = doc(collection(db, 'contentTasks', taskId, 'comments'));
      const commenterName = currentUser.displayName || currentUser.email || 'User';
      batch.set(commentRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: commenterName,
        text,
      });

      const activityLogRef = doc(collection(db, 'contentTasks', taskId, 'activityLog'));
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: commenterName,
        actionType: 'comment_added',
        description: `${commenterName} added a comment: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`,
      });

      const userIdsToNotify = buildUserIdsToNotify();
      addNotificationsToBatch(batch, userIdsToNotify, `${commenterName} commented on content task: "${task.title}"`, 'content_task_comment');

      await batch.commit();
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setCommentError(t.errCommentFailed);
    } finally {
      if (isMounted.current) setIsAddingComment(false);
    }
  };

  // --- Render guards ---
  if (loading) return <div className={styles.loadingIndicator} dir={isRTL ? 'rtl' : 'ltr'}>{t.loadingDetails}</div>;
  if (accessDenied) {
    return <div className={`${styles.pageContainer} ${styles.errorMessage}`} dir={isRTL ? 'rtl' : 'ltr'}>{error}</div>;
  }
  if (error && !task) {
    return <div className={`${styles.pageContainer} ${styles.errorMessage}`} dir={isRTL ? 'rtl' : 'ltr'}>{error}</div>;
  }
  if (!task) {
    return <div className={`${styles.pageContainer} ${styles.errorMessage}`} dir={isRTL ? 'rtl' : 'ltr'}>{t.contentTaskUnavailable}</div>;
  }

  const statusSuffix = getContentStatusClassSuffix(task.status);
  const statusClassName = styles[`status${statusSuffix}`] || styles.statusDefault;
  const canEdit = isManagerUser || isCurrentUserCreator;
  const isTerminal = ['Published', 'Cancelled'].includes(task.status);

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      {error && <div className={styles.errorMessage}>{error}</div>}
      {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

      <div className={styles.detailBox}>
        <div className={styles.header}>
          <h2 className={styles.title}>{task.title || t.untitledTask}</h2>
          <div className={styles.actionButtonsContainer}>
            <LanguageToggle language={language} onToggle={toggleLanguage} />
            {canEdit && !isTerminal && (
              <RouterLink
                to={`/social/tasks/${taskId}/edit`}
                className={`${styles.actionButton} ${styles.editButton}`}
              >
                {t.edit}
              </RouterLink>
            )}
            {cancelRoles.includes(userRole) && !isTerminal && (
              <button
                onClick={handleCancel}
                className={`${styles.actionButton} ${styles.cancelTaskButton}`}
                disabled={isProcessingAction}
              >
                {t.cancelTask}
              </button>
            )}
            <RouterLink to="/social/tasks" className={`${styles.actionButton} ${styles.backButton}`}>
              {t.backToList}
            </RouterLink>
          </div>
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.status}</strong>
            <span className={`${styles.statusBadge} ${statusClassName}`}>{getStatusLabel(task.status, language)}</span>
          </div>
          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.client}</strong>
            <span className={styles.detailValue}>{task.clientName || t.na}</span>
          </div>
          <div className={`${styles.detailItem} ${styles.detailItemFull}`}>
            <strong className={styles.detailLabel}>{t.platform}</strong>
            {taskTargets.length > 0 ? (
              <div className={styles.assigneeList}>
                {taskTargets.map((tg, idx) => {
                  const isPublished = (task.postUrls || []).some((p) => p.pageId === tg.pageId && tg.pageId);
                  return (
                    <span key={`${tg.platform}-${tg.pageId || idx}`} className={styles.assigneeChip}>
                      {getPlatformLabel(tg.platform, language)}
                      {tg.pageName ? ` — ${tg.pageName}` : ` (${t.notLinked})`}
                      {isPublished ? ` ✓ ${t.publishedTag}` : ''}
                    </span>
                  );
                })}
              </div>
            ) : (
              <span className={styles.detailValue}>{t.na}</span>
            )}
          </div>
          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.contentType}</strong>
            <span className={styles.detailValue}>{task.contentType ? getContentTypeLabel(task.contentType, language) : t.na}</span>
          </div>
          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.dueDate}</strong>
            <span className={styles.detailValue}>{formatDate(task.dueDate)}</span>
          </div>
          {task.scheduledDate && (
            <div className={styles.detailItem}>
              <strong className={styles.detailLabel}>{t.scheduledDate}</strong>
              <span className={styles.detailValue}>{formatDate(task.scheduledDate, { dateStyle: 'medium', timeStyle: 'short' })}</span>
            </div>
          )}
          {task.publishedDate && (
            <div className={styles.detailItem}>
              <strong className={styles.detailLabel}>{t.publishedDate}</strong>
              <span className={styles.detailValue}>{formatDate(task.publishedDate, { dateStyle: 'medium', timeStyle: 'short' })}</span>
            </div>
          )}
          {(task.postUrls && task.postUrls.length > 0) ? (
            <div className={`${styles.detailItem} ${styles.detailItemFull}`}>
              <strong className={styles.detailLabel}>{t.postUrl}</strong>
              <div className={styles.assigneeList}>
                {task.postUrls.map((p, idx) => (
                  <a
                    key={`${p.pageId || idx}-${p.postUrl}`}
                    href={p.postUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.detailValue}
                  >
                    {getPlatformLabel(p.platform, language)}: {p.postUrl}
                  </a>
                ))}
              </div>
            </div>
          ) : task.postUrl ? (
            <div className={styles.detailItem}>
              <strong className={styles.detailLabel}>{t.postUrl}</strong>
              <a href={task.postUrl} target="_blank" rel="noopener noreferrer" className={styles.detailValue}>{task.postUrl}</a>
            </div>
          ) : null}

          <div className={styles.detailItemFull}>
            <strong className={styles.detailLabel}>{t.caption}</strong>
            <p className={styles.detailValueDescription}>{task.caption || t.na}</p>
          </div>
          {task.hashtags && (
            <div className={styles.detailItemFull}>
              <strong className={styles.detailLabel}>{t.hashtags}</strong>
              <p className={styles.detailValueDescription}>{task.hashtags}</p>
            </div>
          )}
          {task.cta && (
            <div className={styles.detailItem}>
              <strong className={styles.detailLabel}>{t.cta}</strong>
              <span className={styles.detailValue}>{task.cta}</span>
            </div>
          )}

          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.writer}</strong>
            <span className={styles.detailValue}>{task.writerId ? findUserNameById(task.writerId) : t.unassigned}</span>
          </div>
          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.designer}</strong>
            <span className={styles.detailValue}>{task.designerId ? findUserNameById(task.designerId) : t.unassigned}</span>
          </div>
          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>{t.reviewer}</strong>
            <span className={styles.detailValue}>{task.reviewerId ? findUserNameById(task.reviewerId) : t.unassigned}</span>
          </div>

          <div className={`${styles.detailItem} ${styles.assigneesItem}`}>
            <strong className={styles.detailLabel}>{t.assignedTo}</strong>
            {(task.assignedTo || []).length > 0 ? (
              <div className={styles.assigneeList}>
                {(task.assignedTo || []).map(uid => (
                  <span key={uid} className={styles.assigneeChip}>{findUserNameById(uid)}</span>
                ))}
              </div>
            ) : (
              <span className={styles.detailValue}>{t.none}</span>
            )}
          </div>

          {task.clientApprovalStatus && (
            <div className={styles.detailItem}>
              <strong className={styles.detailLabel}>{t.clientApproval}</strong>
              <span className={styles.detailValue}>{t.clientApprovalStatusLabels[task.clientApprovalStatus] || task.clientApprovalStatus}</span>
            </div>
          )}
          {task.clientNotes && (
            <div className={styles.detailItemFull}>
              <strong className={styles.detailLabel}>{t.clientNotes}</strong>
              <p className={styles.detailValueDescription}>{task.clientNotes}</p>
            </div>
          )}
          {task.internalNotes && (
            <div className={styles.detailItemFull}>
              <strong className={styles.detailLabel}>{t.internalNotes}</strong>
              <p className={styles.detailValueDescription}>{task.internalNotes}</p>
            </div>
          )}
        </div>

        <div className={styles.metaInfo}>
          <span>{t.createdBy} {task.createdBy ? findUserNameById(task.createdBy) : t.na}</span>
          <span title={task.createdAt?.toLocaleString()}>{t.created} {formatRelativeTime(task.createdAt)}</span>
          <span title={task.updatedAt?.toLocaleString()}>{t.lastUpdated} {formatRelativeTime(task.updatedAt)}</span>
        </div>

        {/* --- Workflow Action Buttons --- */}
        {canActOnTask && (
          <div className={styles.workflowSection}>
            <h3 className={styles.subTitle}>{t.workflowActions}</h3>
            <div className={styles.workflowButtons}>
              {task.status === 'New Request' && (
                <button onClick={handleStartWriting} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                  {t.startContentWriting}
                </button>
              )}

              {task.status === 'Content Writing' && (
                <button onClick={handleMarkContentReady} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                  {t.markContentReady}
                </button>
              )}

              {task.status === 'Content Ready' && (
                <>
                  <button onClick={handleSendToDesign} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                    {t.sendToDesignEditing}
                  </button>
                  <button onClick={handleSkipToInternalReview} className={`${styles.actionButton} ${styles.secondaryActionButton}`} disabled={isProcessingAction}>
                    {t.skipDesignSendToInternalReview}
                  </button>
                </>
              )}

              {task.status === 'Design / Editing' && (
                <button onClick={handleMarkDesignReady} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                  {t.markDesignReady}
                </button>
              )}

              {task.status === 'Design Ready' && (
                <button onClick={handleSendToInternalReview} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                  {t.sendToInternalReview}
                </button>
              )}

              {task.status === 'Internal Review' && (
                <>
                  <button onClick={openChangesRequestedForm} className={`${styles.actionButton} ${styles.rejectButton}`} disabled={isProcessingAction}>
                    {t.requestChanges}
                  </button>
                  <button onClick={handleApproveInternally} className={`${styles.actionButton} ${styles.approveButton}`} disabled={isProcessingAction}>
                    {t.approveReadyForClient}
                  </button>
                </>
              )}

              {task.status === 'Changes Requested' && (
                <>
                  <button onClick={handleResumeWriting} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                    {t.resumeContentWriting}
                  </button>
                  <button onClick={handleResumeDesign} className={`${styles.actionButton} ${styles.secondaryActionButton}`} disabled={isProcessingAction}>
                    {t.resumeDesignEditing}
                  </button>
                </>
              )}

              {task.status === 'Ready for Client Approval' && (
                <button onClick={handleSendToClient} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                  {t.sendToClientAwaitingApproval}
                </button>
              )}

              {task.status === 'Client Approval' && (
                <>
                  <button onClick={openClientRejectForm} className={`${styles.actionButton} ${styles.rejectButton}`} disabled={isProcessingAction}>
                    {t.clientRejected}
                  </button>
                  <button onClick={handleClientApprove} className={`${styles.actionButton} ${styles.approveButton}`} disabled={isProcessingAction}>
                    {t.clientApproved}
                  </button>
                </>
              )}

              {task.status === 'Approved' && (
                <>
                  <button onClick={openScheduleForm} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                    {t.schedulePost}
                  </button>
                  {canPublishViaMeta && (
                    <button onClick={handlePublishNowMeta} className={`${styles.actionButton} ${styles.approveButton}`} disabled={isProcessingAction}>
                      {isPublishingMeta ? <span className={styles.loadingSpinnerSmall}></span> : t.publishNowMeta}
                    </button>
                  )}
                </>
              )}

              {task.status === 'Scheduled' && (
                <>
                  <button onClick={openPublishForm} className={`${styles.actionButton} ${styles.primaryActionButton}`} disabled={isProcessingAction}>
                    {t.markAsPublished}
                  </button>
                  {canPublishViaMeta && (
                    <button onClick={handlePublishNowMeta} className={`${styles.actionButton} ${styles.approveButton}`} disabled={isProcessingAction}>
                      {isPublishingMeta ? <span className={styles.loadingSpinnerSmall}></span> : t.publishNowMeta}
                    </button>
                  )}
                </>
              )}

              {task.status === 'Published' && (
                <>
                  {pendingMetaTargets.length > 0 ? (
                    <>
                      <p className={styles.statusLockedMsg}>
                        {t.publishRemainingHint(pendingMetaTargets.map((tg) => getPlatformLabel(tg.platform, language)).join(', '))}
                      </p>
                      <button onClick={handlePublishNowMeta} className={`${styles.actionButton} ${styles.approveButton}`} disabled={isProcessingAction}>
                        {isPublishingMeta ? <span className={styles.loadingSpinnerSmall}></span> : t.publishRemainingMeta}
                      </button>
                    </>
                  ) : (
                    <p className={styles.statusLockedMsg}>{t.publishedLockedMsg}</p>
                  )}
                </>
              )}
              {task.status === 'Cancelled' && (
                <p className={styles.statusLockedMsg}>{t.cancelledLockedMsg}</p>
              )}
            </div>

            {task.metaPublishLastError && (task.status !== 'Published' || pendingMetaTargets.length > 0) && (
              <p className={styles.errorMessage}>{t.metaLastErrorLabel} {task.metaPublishLastError}</p>
            )}

            {/* Inline form: Changes Requested (Internal Review rejection) */}
            {showChangesRequestedForm && (
              <div className={styles.inlineForm}>
                <label htmlFor="changesNote">{t.changesNoteLabel}</label>
                <textarea
                  id="changesNote"
                  value={changesNote}
                  onChange={(e) => setChangesNote(e.target.value)}
                  rows={3}
                  disabled={isProcessingAction}
                  placeholder={t.changesNotePlaceholder}
                />
                <div className={styles.inlineFormActions}>
                  <button type="button" onClick={() => setShowChangesRequestedForm(false)} className={`${styles.actionButton} ${styles.cancelButtonExt}`} disabled={isProcessingAction}>{t.cancel}</button>
                  <button type="button" onClick={submitChangesRequested} className={`${styles.actionButton} ${styles.submitButtonExt}`} disabled={isProcessingAction || !changesNote.trim()}>
                    {isTransitioning ? <span className={styles.loadingSpinnerSmall}></span> : t.submit}
                  </button>
                </div>
              </div>
            )}

            {/* Inline form: Client rejection */}
            {showClientRejectForm && (
              <div className={styles.inlineForm}>
                <label htmlFor="clientRejectNote">{t.clientRejectNoteLabel}</label>
                <textarea
                  id="clientRejectNote"
                  value={clientRejectNote}
                  onChange={(e) => setClientRejectNote(e.target.value)}
                  rows={3}
                  disabled={isProcessingAction}
                  placeholder={t.clientRejectNotePlaceholder}
                />
                <div className={styles.inlineFormActions}>
                  <button type="button" onClick={() => setShowClientRejectForm(false)} className={`${styles.actionButton} ${styles.cancelButtonExt}`} disabled={isProcessingAction}>{t.cancel}</button>
                  <button type="button" onClick={submitClientReject} className={`${styles.actionButton} ${styles.submitButtonExt}`} disabled={isProcessingAction || !clientRejectNote.trim()}>
                    {isTransitioning ? <span className={styles.loadingSpinnerSmall}></span> : t.submit}
                  </button>
                </div>
              </div>
            )}

            {/* Inline form: Schedule */}
            {showScheduleForm && (
              <div className={styles.inlineForm}>
                <label htmlFor="scheduledDateInput">{t.scheduledDateTimeLabel}</label>
                <input
                  type="datetime-local"
                  id="scheduledDateInput"
                  value={scheduledDateInput}
                  onChange={(e) => setScheduledDateInput(e.target.value)}
                  disabled={isProcessingAction}
                />
                <div className={styles.inlineFormActions}>
                  <button type="button" onClick={() => setShowScheduleForm(false)} className={`${styles.actionButton} ${styles.cancelButtonExt}`} disabled={isProcessingAction}>{t.cancel}</button>
                  <button type="button" onClick={submitSchedule} className={`${styles.actionButton} ${styles.submitButtonExt}`} disabled={isProcessingAction || !scheduledDateInput}>
                    {isTransitioning ? <span className={styles.loadingSpinnerSmall}></span> : t.confirmSchedule}
                  </button>
                </div>
              </div>
            )}

            {/* Inline form: Publish */}
            {showPublishForm && (
              <div className={styles.inlineForm}>
                <label htmlFor="postUrlInput">{t.postUrlLabel}</label>
                <input
                  type="url"
                  id="postUrlInput"
                  value={postUrlInput}
                  onChange={(e) => setPostUrlInput(e.target.value)}
                  disabled={isProcessingAction}
                  placeholder={t.postUrlPlaceholder}
                />
                <div className={styles.inlineFormActions}>
                  <button type="button" onClick={() => setShowPublishForm(false)} className={`${styles.actionButton} ${styles.cancelButtonExt}`} disabled={isProcessingAction}>{t.cancel}</button>
                  <button type="button" onClick={submitPublish} className={`${styles.actionButton} ${styles.submitButtonExt}`} disabled={isProcessingAction || !postUrlInput.trim()}>
                    {isTransitioning ? <span className={styles.loadingSpinnerSmall}></span> : t.confirmPublished}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- Media Gallery --- */}
        <div className={styles.mediaSection}>
          <h3 className={styles.subTitle}>{t.mediaSectionTitle((task.mediaUrls || []).length, MAX_UPLOAD_COUNT)}</h3>
          {canActOnTask && (task.mediaUrls || []).length < MAX_UPLOAD_COUNT && (
            <div className={styles.inputGroup}>
              <label htmlFor="mediaUpload">
                {t.uploadImageVideo(MAX_UPLOAD_COUNT, MAX_UPLOAD_SIZE_MB)}
              </label>
              <input
                type="file"
                id="mediaUpload"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,video/*"
                multiple
                disabled={isProcessingAction}
                className={styles.fileInput}
              />
            </div>
          )}
          {isUploadingFile && <p className={styles.loadingIndicatorSmall}>{t.uploading}</p>}

          {(task.mediaUrls || []).length > 0 ? (
            <div className={styles.mediaGallery}>
              {(task.mediaUrls || []).map((media, idx) => (
                <div key={media.path || idx} className={styles.mediaItem}>
                  {media.type?.startsWith('video/') ? (
                    <video src={media.url} controls className={styles.mediaPreview} />
                  ) : (
                    <a href={media.url} target="_blank" rel="noopener noreferrer">
                      <img src={media.url} alt={media.name} className={styles.mediaPreview} />
                    </a>
                  )}
                  <span className={styles.mediaName} title={media.name}>{media.name}</span>
                  {canActOnTask && (
                    <button
                      type="button"
                      onClick={() => handleDeleteMedia(media)}
                      className={styles.mediaDeleteButton}
                      disabled={isProcessingAction}
                      title={t.removeMedia}
                    >
                      {isDeletingMediaPath === media.path ? <span className={styles.loadingSpinnerSmall}></span> : <>&times;</>}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.noMediaMessage}>{t.noMediaUploaded}</p>
          )}
        </div>

        {/* --- Comments --- */}
        <div className={styles.commentsSection}>
          <h3 className={styles.commentsTitle}>{t.commentsSectionTitle(comments.length)}</h3>
          {commentError && <p className={styles.errorMessage}>{commentError}</p>}
          {commentsLoading ? (
            <p className={styles.loadingIndicatorSmall}>{t.loadingComments}</p>
          ) : (
            <ul className={styles.commentList}>
              {comments.length === 0 && <li className={styles.noComments}>{t.noCommentsYet}</li>}
              {comments.map(comment => (
                <li key={comment.id} className={styles.commentItem}>
                  <div className={styles.commentBody}>
                    <div className={styles.commentHeader}>
                      <strong className={styles.commentAuthor}>{comment.userName || t.userFallback}</strong>
                      <span className={styles.commentTimestamp} title={comment.timestamp?.toLocaleString()}>
                        {comment.timestamp ? formatRelativeTime(comment.timestamp) : ''}
                      </span>
                    </div>
                    <p className={styles.commentText}>{comment.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={handleAddComment} className={styles.addCommentForm}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t.addCommentPlaceholder}
              rows={3}
              className={styles.commentInput}
              disabled={isAddingComment || isProcessingAction}
              required
            />
            <button
              type="submit"
              className={styles.commentSubmitButton}
              disabled={isAddingComment || isProcessingAction || !newComment.trim()}
            >
              {isAddingComment ? <span className={styles.loadingSpinnerSmall}></span> : t.addComment}
            </button>
          </form>
        </div>

        {/* --- Activity Log --- */}
        <div className={styles.activityLogSection}>
          <h3 className={styles.activityLogTitle}>{t.activityLogTitle}</h3>
          {activityLoading && <div className={styles.loadingIndicatorSmall}>{t.loadingActivity}</div>}
          {activityError && <p className={styles.errorMessage}>{activityError}</p>}
          {!activityLoading && !activityError && activityLog.length === 0 && (
            <p className={styles.noActivityMessage}>{t.noActivityRecorded}</p>
          )}
          {!activityLoading && !activityError && activityLog.length > 0 && (
            <ul className={styles.activityList}>
              {activityLog.map(entry => (
                <li key={entry.id} className={styles.activityItem}>
                  <div className={styles.activityHeader}>
                    <strong className={styles.activityUser}>{entry.userName || t.systemFallback}</strong>
                    <span className={styles.activityTimestamp} title={entry.timestamp?.toLocaleString()}>
                      {entry.timestamp ? formatRelativeTime(entry.timestamp) : t.na}
                    </span>
                  </div>
                  <p className={styles.activityDescription}>{entry.description}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentTaskDetailPage;
